// Prompt templates — task scaffolds with variable slots.
// Each template can recommend a model count, max_tokens budget, and per-agent
// system prompt presets that bias the agents toward different perspectives.

export const TEMPLATES = [
  {
    id: 'compare-approaches',
    icon: 'GitCompareArrows',
    label: 'Compare approaches',
    blurb: 'Three angles on a tough decision.',
    task: 'Compare 3 distinct approaches to: {topic}.\nFor each approach give: short description, 3 pros, 3 cons, when to choose it, a one-sentence example.',
    suggestedAgents: 3,
    suggestedMaxTokens: 700,
    systemPrompts: [
      'You are a pragmatic engineer. Optimize for shipping speed and operational simplicity.',
      'You are a systems architect. Optimize for long-term flexibility, observability, and correctness.',
      'You are a cost-conscious operator. Optimize for $/value and low ongoing maintenance.',
    ],
    fields: [{ key: 'topic', label: 'Topic / decision', placeholder: 'choosing a queue: SQS vs Kafka vs Redis Streams' }],
  },
  {
    id: 'critique',
    icon: 'PenLine',
    label: 'Critique & improve',
    blurb: 'Two reviewers + a synthesis pass.',
    task: 'Critique the following text. Identify weaknesses, then produce an improved version.\n\n"""\n{text}\n"""',
    suggestedAgents: 2,
    suggestedMaxTokens: 800,
    systemPrompts: [
      'You are a sharp editor. Be ruthless about clarity, structure, and unnecessary words.',
      'You are a domain expert. Check accuracy, missing context, and unsupported claims.',
    ],
    fields: [{ key: 'text', label: 'Text to critique', placeholder: 'paste the draft here…', textarea: true }],
  },
  {
    id: 'code-review',
    icon: 'Code2',
    label: 'Code review',
    blurb: 'Correctness, security, performance.',
    task: 'Review this code for correctness, security, and performance issues. Suggest concrete fixes with code blocks.\n\n```\n{code}\n```',
    suggestedAgents: 3,
    suggestedMaxTokens: 900,
    systemPrompts: [
      'You are a senior engineer. Find bugs, edge cases, and incorrect logic.',
      'You are a security reviewer. Look for OWASP-class issues, injection, auth bypass, secret handling.',
      'You are a performance engineer. Find hot paths, allocations, blocking I/O, and N+1 patterns.',
    ],
    fields: [{ key: 'code', label: 'Code', placeholder: 'paste code…', textarea: true, mono: true }],
  },
  {
    id: 'brainstorm',
    icon: 'Lightbulb',
    label: 'Brainstorm',
    blurb: 'Diverse, specific, actionable.',
    task: 'Brainstorm 10 creative ideas for: {topic}.\nMake them diverse (no two ideas should overlap), specific (concrete enough to act on), and actionable (state the first step).',
    suggestedAgents: 3,
    suggestedMaxTokens: 700,
    systemPrompts: [
      'You are a divergent thinker. Push for unusual angles. No safe answers.',
      'You are a product strategist. Prefer ideas with clear user value and weak competition.',
      'You are a constraint-driven designer. Prefer ideas that work under tight budget and timeline.',
    ],
    fields: [{ key: 'topic', label: 'Topic', placeholder: 'a B2B SaaS for plumbers' }],
  },
  {
    id: 'summarize',
    icon: 'FileText',
    label: 'Summarize',
    blurb: 'Capture the essential ideas.',
    task: 'Summarize the following in 5 bullet points capturing the most important ideas. Add a one-sentence TL;DR.\n\n"""\n{text}\n"""',
    suggestedAgents: 2,
    suggestedMaxTokens: 400,
    systemPrompts: [
      'You are a precise summarizer. No fluff, no opinion, just the structure of what was said.',
      'You are a thoughtful summarizer. Add the implication or "so what" of each key point.',
    ],
    fields: [{ key: 'text', label: 'Text', placeholder: 'paste content…', textarea: true }],
  },
  {
    id: 'translate',
    icon: 'Languages',
    label: 'Translate',
    blurb: 'Tone-preserving translation.',
    task: 'Translate the following to {language}, preserving register, tone, and idiom where possible.\n\n"""\n{text}\n"""',
    suggestedAgents: 2,
    suggestedMaxTokens: 500,
    systemPrompts: [
      'You are a literary translator. Prioritize naturalness and tone in the target language.',
      'You are a technical translator. Prioritize precision and consistency for technical terms.',
    ],
    fields: [
      { key: 'language', label: 'Target language', placeholder: 'Japanese' },
      { key: 'text', label: 'Source text', placeholder: 'paste text…', textarea: true },
    ],
  },
  {
    id: 'plan',
    icon: 'ListChecks',
    label: 'Plan a project',
    blurb: 'Phased plan with risks.',
    task: 'Create a phased project plan for: {project}.\nInclude milestones, dependencies, key risks, and a definition of done.',
    suggestedAgents: 3,
    suggestedMaxTokens: 900,
    systemPrompts: [
      'You are a delivery-focused PM. Prefer the shortest viable path. Explicit dependencies.',
      'You are a risk officer. Surface failure modes and mitigations early.',
      'You are a tech lead. Translate plan into concrete tasks and ownership.',
    ],
    fields: [{ key: 'project', label: 'Project', placeholder: 'migrating monolith to event-driven microservices' }],
  },
  {
    id: 'rfc',
    icon: 'FileCode2',
    label: 'Write an RFC',
    blurb: 'Motivation, design, alternatives.',
    task: 'Write an RFC for: {feature}. Sections: Motivation, Goals & Non-Goals, Design, API/Schema, Alternatives Considered, Drawbacks, Rollout Plan, Open Questions.',
    suggestedAgents: 2,
    suggestedMaxTokens: 1400,
    systemPrompts: [
      'You are an experienced staff engineer writing for fellow engineers. Be precise.',
      'You are a sceptical reviewer. Strengthen weak sections by anticipating objections.',
    ],
    fields: [{ key: 'feature', label: 'Feature', placeholder: 'a typed event bus for our platform' }],
  },
  {
    id: 'extract',
    icon: 'TableProperties',
    label: 'Extract structured data',
    blurb: 'Schema-driven extraction.',
    task: 'Extract structured data from the text below. Output strict JSON matching this schema:\n{schema}\n\nText:\n"""\n{text}\n"""',
    suggestedAgents: 3,
    suggestedMaxTokens: 600,
    systemPrompts: [
      'You are a strict extractor. Output ONLY valid JSON. No prose, no code fences.',
      'You are a thorough extractor. Capture optional fields when they appear.',
      'You are a fact-checker extractor. Refuse to invent data not present in the source.',
    ],
    fields: [
      { key: 'schema', label: 'JSON schema (or example)', placeholder: '{"name":"string","tags":["string"]}', textarea: true, mono: true },
      { key: 'text', label: 'Source text', placeholder: 'paste text…', textarea: true },
    ],
  },
];

export function templateById(id) {
  return TEMPLATES.find((t) => t.id === id) || null;
}

export function fillTemplate(template, values) {
  if (!template) return '';
  let out = template.task;
  for (const f of template.fields || []) {
    const v = (values?.[f.key] ?? '').toString();
    out = out.replaceAll(`{${f.key}}`, v.trim() || `<${f.key}>`);
  }
  return out;
}
